import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import type { ApplicationConfig } from '@angular/core';
import { ErrorHandler, inject, isDevMode, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router } from '@angular/router';

import { authInterceptor } from '@rpgc-ai/services/auth/auth.interceptor';
import { SOCIAL_LINKS_SERVICE } from '@rpgc-ai/services/social-link/social-link.const';
import { SocialLinkService } from '@rpgc-ai/services/social-link/social-link.service';
import { SUPPORTED_LANGUAGES_CODE } from '@rpgc-ai/services/translate/languages';
import { TranslocoHttpLoader } from '@rpgc-ai/services/translate/transloco-loader';

import { IS_RPGC } from '@rpgc-ai/environments';

import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { provideHighcharts } from 'highcharts-angular';

import { appRoutes } from './app.routes';

// Регистрация РУ локали, чтобы в angular-пайпах можно было использовать ru-RU
registerLocaleData(localeRu);

// Определяем базовых провайдеров, которые будут во всех режимах
const baseProviders = [
  provideZonelessChangeDetection(),
  provideAnimationsAsync(),
  provideRouter(appRoutes),
  provideStoreDevtools({
    maxAge: 25, // Количество сохраняемых состояний
    logOnly: !isDevMode(), // Ограничение для продакшена
    autoPause: true, // Автоматическая пауза при навигации
    trace: false, // Трассировка действий
    traceLimit: 75, // Лимит трассировки
    connectInZone: false, // Отключаем подключение в зоне Angular, т.к. используем zoneless режим
  }),
  provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
  provideTransloco({
    config: {
      availableLangs: SUPPORTED_LANGUAGES_CODE,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
      missingHandler: {
        useFallbackTranslation: false,
      },
    },
    loader: TranslocoHttpLoader,
  }),
  provideTranslocoPersistLang({
    storage: { useFactory: storageFactory },
    storageKey: 'selectedLanguage',
    getLangFn: ({ cachedLang, browserLang, defaultLang }) => {
      return cachedLang || browserLang || defaultLang;
    },
  }),
  provideHighcharts(),
  { provide: IS_RPGC, useValue: false },
];

// Определяем провайдеры Sentry, которые будут только в production
const sentryProviders = isDevMode()
  ? []
  : [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        // Получаем сервис через inject (просто для инициализации)
        inject(Sentry.TraceService);
        // Функция должна возвращать void, Promise или Observable
        // Возвращаем void (не функцию)
      }),
    ];

const customProviders = [{ provide: SOCIAL_LINKS_SERVICE, useClass: SocialLinkService }];

export const appConfig: ApplicationConfig = {
  providers: [...baseProviders, ...sentryProviders, ...customProviders],
};

function storageFactory(): Storage {
  return localStorage;
}
